import { emit, listen } from "@tauri-apps/api/event";
import { getAllWindows, getCurrentWindow } from "@tauri-apps/api/window";
import { create } from "zustand";
import { isTauri } from "@/lib/is-tauri";
import type { RunIdentity, VisibleConversation } from "@/lib/run-surface-owner";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
  type DesktopChatAuthScope,
} from "./desktop-chat-auth-scope";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";

export const RUN_SURFACE_REPORT_EVENT = "denker://run-surface-report";
export const RUN_SURFACE_REQUEST_EVENT = "denker://run-surface-request";
const MAX_WINDOWS = 64;
const MAX_SURFACES = 256;

export interface RenderedCursorRun extends RunIdentity {
  awaitingInputId?: string;
}

export interface RunSurfaceReport {
  windowLabel: string;
  epoch: number;
  revision: number;
  authScope: DesktopChatAuthScope | null;
  conversations: readonly VisibleConversation[];
  cursors: readonly RenderedCursorRun[];
  released?: boolean;
}

export const useRunSurfaceRegistry = create<{
  reports: Record<string, RunSurfaceReport>;
}>(() => ({ reports: {} }));

const retiredEpochs = new Map<string, number>();
const destructionWatches = new Map<string, { unlisten: (() => void) | null }>();
const myLabel = () => (isTauri() ? getCurrentWindow().label : "browser");
const localEpoch = performance.timeOrigin + performance.now();
let revision = 0;
let local: Pick<RunSurfaceReport, "conversations" | "cursors" | "authScope"> = {
  conversations: [],
  cursors: [],
  authScope: null,
};
let signature = "";
let references = 0;
let detach: (() => void) | null = null;

function retireWindow(label: string) {
  const report = useRunSurfaceRegistry.getState().reports[label];
  if (report) {
    if (retiredEpochs.size < MAX_WINDOWS || retiredEpochs.has(label))
      retiredEpochs.set(label, report.epoch);
    const reports = { ...useRunSurfaceRegistry.getState().reports };
    delete reports[label];
    useRunSurfaceRegistry.setState({ reports });
  }
  const unlisten = destructionWatches.get(label)?.unlisten;
  if (unlisten) safeTauriUnlisten(unlisten);
  destructionWatches.delete(label);
}

function watchDestruction(label: string) {
  if (!isTauri() || references === 0 || destructionWatches.has(label)) return;
  const scope = getDesktopChatAuthScope();
  const watch = { unlisten: null as (() => void) | null };
  destructionWatches.set(label, watch);
  void listen(
    "tauri://destroyed",
    () => {
      if (destructionWatches.get(label) === watch) retireWindow(label);
    },
    { target: { kind: "Window", label } },
  )
    .then((unlisten) => {
      if (
        destructionWatches.get(label) !== watch ||
        !isCurrentDesktopChatAuthScope(scope)
      ) {
        safeTauriUnlisten(unlisten);
        return;
      }
      watch.unlisten = unlisten;
      // Covers destruction between the report and listener registration.
      void pruneClosedRunSurfaceWindows();
    })
    .catch(() => destructionWatches.delete(label));
}

function clearDestructionWatches() {
  destructionWatches.forEach((watch) => {
    if (watch.unlisten) safeTauriUnlisten(watch.unlisten);
  });
  destructionWatches.clear();
}

function validIdentity(value: unknown): value is RunIdentity {
  if (!value || typeof value !== "object") return false;
  const identity = value as Record<string, unknown>;
  const keys = [
    "conversationId",
    "executionId",
    "runtimeSessionId",
    "conversationFrameId",
  ];
  return (
    keys.every(
      (key) =>
        identity[key] === undefined ||
        identity[key] === null ||
        (typeof identity[key] === "string" && identity[key].length <= 256),
    ) &&
    keys.some(
      (key) => typeof identity[key] === "string" && identity[key].length > 0,
    )
  );
}

function validConversation(value: unknown): value is VisibleConversation {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as VisibleConversation).frameId === "string" &&
    (value as VisibleConversation).frameId.length > 0 &&
    (value as VisibleConversation).frameId.length <= 256 &&
    validIdentity({
      ...value,
      conversationFrameId: (value as VisibleConversation).frameId,
    })
  );
}

function validCursor(value: unknown): value is RenderedCursorRun {
  return (
    validIdentity(value) &&
    ((value as RenderedCursorRun).awaitingInputId === undefined ||
      (typeof (value as RenderedCursorRun).awaitingInputId === "string" &&
        (value as RenderedCursorRun).awaitingInputId!.length <= 256))
  );
}

/** Reordered updates and reports from a previous login/page cannot own UI. */
export function applyRunSurfaceReport(report: RunSurfaceReport): boolean {
  if (
    !report ||
    typeof report !== "object" ||
    !isCurrentDesktopChatAuthScope(report.authScope) ||
    typeof report.windowLabel !== "string" ||
    !report.windowLabel ||
    report.windowLabel.length > 128 ||
    report.windowLabel === myLabel() ||
    !Number.isFinite(report.epoch) ||
    !Number.isSafeInteger(report.revision) ||
    !Array.isArray(report.conversations) ||
    !Array.isArray(report.cursors) ||
    report.conversations.length > MAX_SURFACES ||
    report.cursors.length > MAX_SURFACES ||
    !report.conversations.every(validConversation) ||
    !report.cursors.every(validCursor) ||
    (report.released !== undefined && typeof report.released !== "boolean") ||
    report.epoch <= (retiredEpochs.get(report.windowLabel) ?? -Infinity)
  )
    return false;
  const reports = useRunSurfaceRegistry.getState().reports;
  const previous = reports[report.windowLabel];
  if (
    previous &&
    (report.epoch < previous.epoch ||
      (report.epoch === previous.epoch && report.revision <= previous.revision))
  )
    return false;
  if (!previous && Object.keys(reports).length >= MAX_WINDOWS) return false;
  useRunSurfaceRegistry.setState({
    reports: { ...reports, [report.windowLabel]: report },
  });
  watchDestruction(report.windowLabel);
  return true;
}

export function unionRunSurfaces(
  conversations: readonly VisibleConversation[],
  cursors: readonly RenderedCursorRun[],
  reports = useRunSurfaceRegistry.getState().reports,
): { conversations: VisibleConversation[]; cursors: RenderedCursorRun[] } {
  const visibleFrames = new Map(
    conversations.map((frame) => [JSON.stringify(frame), frame]),
  );
  const visibleCursors = new Map(
    cursors.map((cursor) => [JSON.stringify(cursor), cursor]),
  );
  for (const report of Object.values(reports)) {
    if (report.released || !isCurrentDesktopChatAuthScope(report.authScope))
      continue;
    for (const frame of report.conversations)
      visibleFrames.set(JSON.stringify(frame), frame);
    for (const cursor of report.cursors)
      visibleCursors.set(JSON.stringify(cursor), cursor);
  }
  return {
    conversations: [...visibleFrames.values()],
    cursors: [...visibleCursors.values()],
  };
}

function announce(released = false) {
  if (!isTauri() || !isCurrentDesktopChatAuthScope(local.authScope)) return;
  const report: RunSurfaceReport = {
    windowLabel: myLabel(),
    epoch: localEpoch,
    revision: ++revision,
    ...local,
    ...(released ? { released: true, conversations: [], cursors: [] } : {}),
  };
  void emit(RUN_SURFACE_REPORT_EVENT, report).catch(() => {});
}

export function publishLocalRunSurfaces(
  conversations: readonly VisibleConversation[],
  cursors: readonly RenderedCursorRun[],
) {
  const authScope = getDesktopChatAuthScope();
  const next = {
    conversations: conversations.slice(0, MAX_SURFACES),
    cursors: cursors.slice(0, MAX_SURFACES),
    authScope,
  };
  const nextSignature = JSON.stringify(next);
  if (signature === nextSignature) return;
  signature = nextSignature;
  local = next;
  if (references > 0) announce();
}

/** A roster event, rather than a timer, releases forcibly destroyed webviews. */
export async function pruneClosedRunSurfaceWindows() {
  if (!isTauri()) return;
  const scope = getDesktopChatAuthScope();
  try {
    if (typeof getAllWindows !== "function") return;
    const windows = await getAllWindows();
    if (!isCurrentDesktopChatAuthScope(scope)) return;
    const alive = new Set(windows.map((window) => window.label));
    for (const label of Object.keys(useRunSurfaceRegistry.getState().reports)) {
      if (alive.has(label)) continue;
      retireWindow(label);
    }
  } catch {
    // A native roster may change again while windows are being reconciled.
  }
}

/** Shared by the surface consumers in one webview; the final unmount releases it. */
export function attachRunSurfaceRegistry(): () => void {
  if (!isTauri()) return () => {};
  references += 1;
  if (references === 1) {
    let disposed = false;
    let roster = "";
    const unlisteners: Array<() => void> = [];
    const track = <T>(event: string, callback: (payload: T) => void) =>
      listen<T>(event, ({ payload }) => callback(payload)).then((unlisten) => {
        if (disposed) safeTauriUnlisten(unlisten);
        else unlisteners.push(unlisten);
      });
    const request = () =>
      void emit(RUN_SURFACE_REQUEST_EVENT, {
        windowLabel: myLabel(),
        authScope: getDesktopChatAuthScope(),
      }).catch(() => {});
    const reset = subscribeDesktopChatAuthScope(() => {
      clearDestructionWatches();
      retiredEpochs.clear();
      local = {
        conversations: [],
        cursors: [],
        authScope: getDesktopChatAuthScope(),
      };
      signature = "";
      useRunSurfaceRegistry.setState({ reports: {} });
      announce();
      request();
    });
    const release = () => announce(true);
    window.addEventListener("pagehide", release);
    window.addEventListener("beforeunload", release);
    void Promise.all([
      track<RunSurfaceReport>(RUN_SURFACE_REPORT_EVENT, applyRunSurfaceReport),
      track<{ windowLabel: string; authScope: DesktopChatAuthScope | null }>(
        RUN_SURFACE_REQUEST_EVENT,
        (payload) => {
          if (
            payload &&
            payload.windowLabel !== myLabel() &&
            isCurrentDesktopChatAuthScope(payload.authScope)
          )
            announce();
        },
      ),
      track<{ displayIds?: number[] }>("cursor-position", (payload) => {
        if (
          !payload ||
          !Array.isArray(payload.displayIds) ||
          !payload.displayIds.every(Number.isFinite)
        )
          return;
        const next = [...payload.displayIds].sort((a, b) => a - b).join(",");
        if (next === roster) return;
        roster = next;
        void pruneClosedRunSurfaceWindows();
      }),
      track(
        "denker://display-metrics-changed",
        () => void pruneClosedRunSurfaceWindows(),
      ),
    ])
      .then(() => {
        if (disposed) return;
        announce();
        request();
      })
      .catch(() => {});
    detach = () => {
      disposed = true;
      release();
      reset();
      window.removeEventListener("pagehide", release);
      window.removeEventListener("beforeunload", release);
      unlisteners.forEach(safeTauriUnlisten);
      clearDestructionWatches();
      retiredEpochs.clear();
      signature = "";
      local = { conversations: [], cursors: [], authScope: null };
      useRunSurfaceRegistry.setState({ reports: {} });
    };
  }
  let attached = true;
  return () => {
    if (!attached) return;
    attached = false;
    references -= 1;
    if (references === 0) {
      detach?.();
      detach = null;
    }
  };
}
